import { FeatureFlagKey } from "@redotech/feature-flags-model/keys";
import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import { RedoPlatformAuthRpcClientContext } from "@redotech/redo-merchant-app-common/platform/redo-platform-rpc-client-provider";
import { RedoSupportAuthRpcClientContext } from "@redotech/redo-merchant-app-common/support/redo-support-rpc-client-provider";
import { CustomerSuggestionCandidate } from "@redotech/redo-merchant-support-rpc/schema/get-customer-suggestions";
import { MinimalConversation } from "@redotech/redo-model/conversation";
import { useContext } from "react";

export interface UseCustomerSuggestionsResult {
  suggestions: CustomerSuggestionCandidate[];
  loading: boolean;
  error: Error | undefined;
}

export function useCustomerSuggestions(
  conversation: MinimalConversation | undefined,
): UseCustomerSuggestionsResult {
  const supportRpcClient = useRequiredContext(RedoSupportAuthRpcClientContext);
  const platformRpcClient = useContext(RedoPlatformAuthRpcClientContext);

  const result = useLoad(async () => {
    if (!conversation || !supportRpcClient || !platformRpcClient) {
      return { suggestions: [], featureFlagEnabled: false };
    }

    const featureFlagResult = await platformRpcClient.getFeatureFlag({
      flagKey: FeatureFlagKey.CUSTOMER_SUGGESTIONS,
    });

    if (!featureFlagResult.enabled) {
      return { suggestions: [], featureFlagEnabled: false };
    }

    const response = await supportRpcClient.getCustomerSuggestions({
      conversationId: conversation._id,
    });

    return { ...response, featureFlagEnabled: true };
  }, [conversation?._id, supportRpcClient, platformRpcClient]);

  if (result.pending) {
    return { suggestions: [], loading: true, error: undefined };
  }

  if (result.error) {
    return { suggestions: [], loading: false, error: result.error };
  }

  if (!result.value) {
    return { suggestions: [], loading: false, error: undefined };
  }

  return {
    suggestions: result.value.suggestions,
    loading: false,
    error: undefined,
  };
}
